import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import React, { useEffect, useRef } from 'react';

import PageSection from '../../PageSections/PageSection';
import Bleed from './Bleed';
import Cards from './Cards';
import OverlappingBleed from './OverlappingBleed';
import * as styles from './SideScroll.module.scss';
import Snippets from './Snippets';

gsap.registerPlugin(ScrollTrigger);

export default function SideScroll({
  section,
  start = null,
  ignoreMobileResize = false,
  slim = true,
  refresh = false,
}) {
  const component = useRef();
  const trigger = useRef();
  const content = useRef();

  useEffect(() => {
    ScrollTrigger.config({ ignoreMobileResize });

    if (refresh) ScrollTrigger.refresh();

    const ctx = gsap.context(() => {
      gsap.to(content.current, {
        x: () => `-${content.current?.scrollWidth - content.current?.clientWidth}px`,
        ease: 'none',
        scrollTrigger: {
          trigger: trigger.current,
          start: () => (start ? `top ${start}` : 'top 10%'),
          end: () => '+=' + content.current?.scrollWidth,
          scrub: true,
          anticipatePin: 1,
          pin: document.querySelector('#layout-body'),
        },
      });
    }, component);

    return () => ctx.revert();
  }, [component, trigger, content, refresh, start, ignoreMobileResize]);

  let scrollingContent;

  switch (section.scrollingContentType) {
    case 'bleed':
      scrollingContent = <Bleed content={section.content} contentRef={content} />;
      break;

    case 'cards':
      scrollingContent = <Cards content={section.content} contentRef={content} />;
      break;

    case 'snippets':
      scrollingContent = <Snippets content={section.content} contentRef={content} />;
      break;

    case 'overlapping bleed': {
      scrollingContent = <OverlappingBleed content={section.content} contentRef={content} />;
      break;
    }
    default:
      break;
  }

  return (
    <div ref={trigger}>
      <PageSection
        data-analytics-section={'scrolling-content'}
        slim={slim}
        className={[styles.sideScroll, styles[section.backgroundColor?.replace(' ', '')]].join(' ')}
      >
        {section.title && <div className={styles.superheading}>{section.title}</div>}
        {section.heading && (section.headingSize === 'H2' ? <h2>{section.heading}</h2> : <h3>{section.heading}</h3>)}
        <div ref={component}>{scrollingContent}</div>
      </PageSection>
    </div>
  );
}
